<template>
  <article class="chart-card" :class="{ 'chart-compact': compact }">
    <h3 class="hbox items-center gap-1">
      <AppIcon :name="icono" size="h-4 w-4" />
      {{ titulo }}
    </h3>
    <p v-if="!tieneDatos" class="vacio">Sin datos para este año.</p>
    <apexchart v-else type="bar" :height="compact ? alturaCompacta : 280" :options="options" :series="series" />
  </article>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import AppIcon from '@/components/ui/AppIcon.vue'
import { formatMoney } from '@/dashboard/mapDashboard'

const MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

function alturaDisponible() {
  if (typeof window === 'undefined' || window.innerWidth < 1024) return 205
  return Math.min(360, Math.max(205, window.innerHeight - 400))
}

export default {
  name: 'YearChart',
  components: { AppIcon, apexchart: VueApexCharts },
  props: {
    meses: { type: Array, default: () => [] },
    titulo: { type: String, required: true },
    icono: { type: String, default: 'chart' },
    color: { type: String, default: '#0A4F9C' },
    dinero: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
  },
  data() {
    return { alturaCompacta: alturaDisponible() }
  },
  mounted() {
    window.addEventListener('resize', this.actualizarAltura)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.actualizarAltura)
  },
  computed: {
    tieneDatos() {
      return this.puntos.some((item) => item.total != null)
    },
    puntos() {
      const ahora = new Date()
      const year = ahora.getFullYear()
      const mesActual = ahora.getMonth() + 1
      const mapa = new Map()
      for (const item of this.meses || []) {
        mapa.set(`${item.year}-${item.month}`, Number(item.total) || 0)
      }
      return MES.map((label, index) => {
        const month = index + 1
        const futuro = month > mesActual
        const conocido = mapa.has(`${year}-${month}`)
        const total = futuro || !conocido ? null : mapa.get(`${year}-${month}`)
        const prevMonth = month === 1 ? 12 : month - 1
        const prevYear = month === 1 ? year - 1 : year
        const previo = mapa.has(`${prevYear}-${prevMonth}`) ? mapa.get(`${prevYear}-${prevMonth}`) : null
        return { label, total, previo }
      })
    },
    series() {
      return [{ name: this.titulo, data: this.puntos.map((item) => item.total) }]
    },
    options() {
      const puntos = this.puntos
      const cifra = (value) => this.cifra(value)
      return {
        chart: {
          fontFamily: 'Inter, Roboto, sans-serif',
          toolbar: { show: false },
          animations: { enabled: true, speed: 200 },
          parentHeightOffset: 0,
        },
        colors: [this.color],
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: '52%' },
        },
        dataLabels: { enabled: false },
        legend: {
          show: !this.compact,
          fontSize: '14px',
          labels: { colors: '#3D4B5F' },
        },
        grid: {
          borderColor: '#D8E0EA',
          strokeDashArray: 0,
          padding: { left: 8, right: 8 },
        },
        xaxis: {
          categories: puntos.map((item) => item.label),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: '#526273', fontSize: this.compact ? '11px' : '12px' } },
        },
        yaxis: {
          labels: {
            style: { colors: '#526273', fontSize: this.compact ? '11px' : '12px' },
            formatter: cifra,
          },
        },
        tooltip: {
          y: {
            formatter(value, opts) {
              if (value == null) return 'Sin dato'
              const punto = puntos[opts.dataPointIndex]
              const base = cifra(value)
              if (punto.previo == null) return base
              const diff = value - punto.previo
              if (diff > 0) return `${base} · subió ${cifra(diff)}`
              if (diff < 0) return `${base} · bajó ${cifra(Math.abs(diff))}`
              return `${base} · igual que el mes anterior`
            },
          },
        },
      }
    },
  },
  methods: {
    actualizarAltura() {
      this.alturaCompacta = alturaDisponible()
    },
    cifra(value) {
      const n = Number(value) || 0
      if (!this.dinero) return String(Math.round(n))
      if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
      if (Math.abs(n) >= 1000) return `$${Math.round(n / 1000)}k`
      return formatMoney(n)
    },
  },
}
</script>

<style scoped>
.chart-card {
  min-height: 320px;
  padding: 16px 12px 8px;
  border: 1px solid #d8e0ea;
  border-radius: 16px;
  background: #fff;
}
.chart-compact { min-height: 0; padding: 10px 12px 2px; border-color: #e4e9ef; box-shadow: 0 4px 15px rgba(16, 42, 67, .035); }
h3 {
  margin: 0 8px 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #0b1b2e;
}
.chart-compact h3 { margin-bottom: 2px; font-size: 14px; line-height: 20px; }
.vacio {
  margin: 24px 8px;
  font-size: 16px;
  color: #3d4b5f;
}
</style>
